import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from 'lib/httpClient';
import { ApiResponse } from 'further-types/api';
import { Api } from 'further-types/companies';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';
import { addHttps } from 'utils/strings';
import { useNotification } from 'hooks/ui/useNotification';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';

type CompanyQueryKey = ['company', { params: { companyId: string } }];

const fetchCompany = async ({ queryKey }: { queryKey: CompanyQueryKey }) => {
  const [, { params }] = queryKey;
  const { data } = await api.get<ApiResponse<Api.GetInvesteeResponse>>(
    `investee/list/${params.companyId}`,
  );
  return data.data;
};

function useCompany(companyId: string, refetchOnWindowFocus?: boolean) {
  return useQuery({
    queryKey: ['company', { params: { companyId } }],
    queryFn: fetchCompany,
    placeholderData: keepPreviousData,
    staleTime: refetchOnWindowFocus ? 0 : QUERY_STALE_TIME_MS,
    enabled: !!companyId,
    refetchOnWindowFocus,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    mutationFn: (data: Api.UpdateInvesteeRequest) => {
      const payload = {
        ...data,
        url: addHttps(data.url),
        linkedinUrl: addHttps(data.linkedinUrl),
        twitterUrl: addHttps(data.twitterUrl),
      };
      return api.post<ApiResponse>(`investee/create`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      queryClient.invalidateQueries({ queryKey: ['groupedCompanies'] });
    },
    onError: (error) => {
      handleApiException(error);
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  const handleApiException = useApiExceptionHandler();

  return useMutation({
    mutationFn: (data: Api.UpdateInvesteeRequest) => {
      const payload = {
        ...data,
        url: addHttps(data.url),
        linkedinUrl: addHttps(data.linkedinUrl),
        twitterUrl: addHttps(data.twitterUrl),
      };
      return api.put<ApiResponse>(`investee/update/${data.id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      queryClient.invalidateQueries({ queryKey: ['groupedCompanies'] });
    },
    onError: (error) => {
      handleApiException(error);
    },
  });
}

export function useDeleteCompany() {
  const notification = useNotification();
  const handleApiException = useApiExceptionHandler();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<ApiResponse>(`investee/delete/${id}`);
      return response.data;
    },
    onSuccess: (data) => {
      notification.success(data?.responseMsg ?? '');
      queryClient.invalidateQueries({ queryKey: ['company'] });
      queryClient.invalidateQueries({ queryKey: ['groupedCompanies'] });
    },
    onError: (error) => {
      handleApiException(error);
    },
  });
}

export function useDeleteDividend() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const handleApiException = useApiExceptionHandler();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<ApiResponse>(
        `investee/delete-dividend/${id}`,
      );
      return response.data;
    },
    onSuccess: (data) => {
      notification.success(data?.responseMsg ?? '');
      queryClient.invalidateQueries({ queryKey: ['company'] });
    },
    onError: (error) => {
      handleApiException(error);
    },
  });
}

export function useDeleteNews() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const handleApiException = useApiExceptionHandler();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<ApiResponse>(
        `investee/delete-new/${id}`,
      );
      return response.data;
    },
    onSuccess: (data) => {
      notification.success(data?.responseMsg ?? '');
      queryClient.invalidateQueries({ queryKey: ['company'] });
    },
    onError: (error) => {
      handleApiException(error);
    },
  });
}

export default useCompany;
